import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { extractErrorMessage } from "../lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative">
      <div className="w-full max-w-sm animate-fade-up">
        {/* Brand block */}
        <div className="flex flex-col items-center text-center mb-10">
          <img src="/cme-logo.png" alt="CME" className="h-12 w-auto mb-5" style={{ filter: "brightness(1.1)" }} />
          <div className="font-mono text-envicom-text text-2xl font-bold tracking-tight">enviCoM 4.0</div>
          <div className="text-envicom-muted text-xs font-mono uppercase tracking-[0.22em] mt-1">
            Engineer Portal
          </div>
        </div>

        {/* Form card */}
        <form
          onSubmit={onSubmit}
          data-testid="login-form"
          className="bg-envicom-surface border border-envicom-border rounded-md p-7"
        >
          <h2 className="font-mono text-lg font-semibold text-envicom-text">Sign in</h2>
          <p className="text-envicom-muted text-sm mt-1">
            Use the credentials issued by your super admin.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label className="text-envicom-muted text-xs uppercase tracking-wider font-mono" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-envicom-text placeholder:text-envicom-muted/50 focus:outline-none focus:border-envicom-accent focus:ring-1 focus:ring-envicom-accent/40"
                placeholder="you@cmenvirosystems.com"
              />
            </div>
            <div>
              <label className="text-envicom-muted text-xs uppercase tracking-wider font-mono" htmlFor="password">
                Password
              </label>
              <input
                id="password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-envicom-input border border-envicom-border rounded px-3 py-2.5 text-envicom-text placeholder:text-envicom-muted/50 focus:outline-none focus:border-envicom-accent focus:ring-1 focus:ring-envicom-accent/40"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div data-testid="login-error" className="mt-4 px-3 py-2 rounded bg-envicom-error/10 border border-envicom-error/30 text-envicom-error text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="mt-6 w-full bg-envicom-accent hover:bg-sky-400 disabled:opacity-50 text-black font-semibold py-2.5 rounded transition-colors flex items-center justify-center gap-2 font-mono"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>

        <p className="mt-6 text-center text-envicom-muted text-xs font-mono">
          Need access? Contact your CM Envirosystems super admin.
        </p>
      </div>
    </div>
  );
}
